import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  RotateCcw,
  Check,
  X,
  ArrowRight,
  ShieldCheck,
  ShieldAlert,
  Mail,
  FileText,
  Tag,
  Landmark,
  BookOpen,
  Clock,
  ArrowUpRight,
  AlertTriangle,
  History,
} from "lucide-react";
import { IMAGES } from "@/data/content";
import { useNoIndex } from "@/lib/useNoIndex";
import { useReelTimeline } from "@/components/demo/useReelTimeline";
import { DemoPlayerBar } from "@/components/demo/DemoPlayerBar";
import { DemoEndSlate } from "@/components/DemoEndSlate";

/**
 * DemoAdminStory — 60-second auto-playing walkthrough of admin operations.
 *
 * Route: /demo/admin-story
 *
 * Board-facing companion to /demo/story. Where Zohran's reel shows the
 * *student* experience, this reel shows what happens after — the review
 * queue, the six automated checks, the fraud flag playbook, the audit
 * trail, and the disbursement schedule. The point is fiduciary calm:
 * every dollar tracked, every decision logged, every student verified.
 *
 * Acts:
 *   01  The review queue (12 pending, sorted by wait)
 *   02  Six automated checks (NSC, .edu, story, docs, category, payout)
 *   03  Fraud flag (enrollment mismatch, story similarity, escalation)
 *   04  Approved with audit trail (every action timestamped)
 *   05  Disbursement schedule (weekly, not lump sum)
 *   06  End slate (yellow watermark, same as /demo/story)
 */

const TOTAL_SECONDS = 60;

const ACTS = [
  {
    id: "01",
    startAt: 0,
    duration: 10,
    kicker: null,
    title: "Every campaign lands here first.",
    subtitle:
      "Twelve pending, sorted by wait time. Nothing goes live without a review.",
  },
  {
    id: "02",
    startAt: 10,
    duration: 12,
    kicker: null,
    title: "Six checks before a human sees it.",
    subtitle:
      "Enrollment, email, story, documents, category, payout account. All green before it hits Terri's queue.",
  },
  {
    id: "03",
    startAt: 22,
    duration: 12,
    kicker: null,
    title: "When something's off, the system says so.",
    subtitle:
      "Enrollment mismatches, duplicate stories, and unusual asks get quarantined for a second look.",
  },
  {
    id: "04",
    startAt: 34,
    duration: 10,
    kicker: null,
    title: "Approved. Live. Logged.",
    subtitle:
      "Reviewer, timestamp, and reasoning captured on every action. Nothing moves without a paper trail.",
  },
  {
    id: "05",
    startAt: 44,
    duration: 10,
    kicker: null,
    title: "Weekly installments, not lump sums.",
    subtitle:
      "Grants disburse across four Fridays to a student's Stripe account. Paused instantly if a flag reopens.",
  },
  {
    id: "06",
    startAt: 54,
    duration: 6,
    kicker: null,
    title:
      "Every dollar tracked. Every decision logged. Every student verified.",
    subtitle: null,
  },
];

export default function DemoAdminStory() {
  useNoIndex();

  const timeline = useReelTimeline(ACTS, TOTAL_SECONDS);
  const { elapsed, ended, currentAct, actProgress, restart } = timeline;

  return (
    <main
      data-testid="demo-admin-story-page"
      className="relative min-h-screen overflow-hidden bg-sand text-ink"
    >
      {/* Warm ambient tint — matches the hero band's warm-sand mood */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(3,34,244,0.35) 0, transparent 45%), radial-gradient(circle at 80% 80%, rgba(253,208,6,0.40) 0, transparent 40%)",
        }}
      />
      {/* Repeating brand-mark pattern watermark — same treatment as the
          hero band uses (Hero.jsx). Tiled full-screen at 3.5% opacity
          with a multiply blend so it reads as a whisper of brand
          behind every scene, never competing with the acts. */}
      <div
        aria-hidden
        data-testid="demo-admin-story-pattern-watermark"
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage: `url(${IMAGES.pattern})`,
          backgroundSize: "460px",
          backgroundRepeat: "repeat",
        }}
      />

      <header className="relative z-20 flex items-center justify-between px-6 py-5 lg:px-10">
        <Link
          to="/demo"
          data-testid="demo-admin-story-exit"
          className="text-[10px] font-semibold uppercase tracking-[0.28em] text-olive transition hover:text-ink"
        >
          ← back to /demo
        </Link>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-olive">
          <span>Board demo</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span>Admin operations</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span data-testid="demo-admin-story-scene-index">
            {currentAct.id} / {String(ACTS.length).padStart(2, "0")}
          </span>
        </div>
      </header>

      <section className="relative z-10 mx-auto flex min-h-[calc(100vh-9rem)] max-w-7xl items-center px-6 pb-24 lg:px-10">
        <AnimatePresence mode="wait">
          {currentAct.id === "06" ? (
            <motion.div
              key="end-slate"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
              data-testid={`demo-admin-story-act-${currentAct.id}`}
            >
              <DemoEndSlate onReplay={restart} />
            </motion.div>
          ) : (
            <motion.div
              key={currentAct.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-[1.35fr_1fr] lg:gap-14"
              data-testid={`demo-admin-story-act-${currentAct.id}`}
            >
              <SceneVisual act={currentAct} actProgress={actProgress} />
              <SceneCopy act={currentAct} ended={ended} onReplay={restart} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <DemoPlayerBar acts={ACTS} totalSeconds={TOTAL_SECONDS} timeline={timeline} testid="demo-admin-story" />

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 z-10 h-[3px] bg-ink/10"
      >
        <div
          data-testid="demo-admin-story-progress"
          className="h-full bg-yellow"
          style={{
            width: `${Math.min(100, (elapsed / TOTAL_SECONDS) * 100)}%`,
            transition: "width 60ms linear",
          }}
        />
      </div>

      <p className="sr-only" aria-live="polite">
        {currentAct.title}
      </p>
    </main>
  );
}

/* ─── Shared primitives ─────────────────────────────────────────────── */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const BrowserChrome = ({ children, url = "myrichaunt.org/admin" }) => (
  <div className="relative w-full overflow-hidden rounded-2xl bg-bone text-ink shadow-[0_40px_100px_-40px_rgba(0,0,0,0.55)]">
    <div className="flex items-center gap-1.5 border-b border-border bg-sand/70 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
        {url}
      </span>
      <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-olive">
        <span className="h-1.5 w-1.5 rounded-full bg-grass" />
        admin · terri.james
      </span>
    </div>
    {children}
  </div>
);

/* ─── Scene switch ──────────────────────────────────────────────────── */

const SceneVisual = ({ act, actProgress }) => {
  switch (act.id) {
    case "01": return <ActQueue actProgress={actProgress} />;
    case "02": return <ActChecks actProgress={actProgress} />;
    case "03": return <ActFraudFlag actProgress={actProgress} />;
    case "04": return <ActAuditTrail actProgress={actProgress} />;
    case "05": return <ActDisbursement actProgress={actProgress} />;
    default: return null;
  }
};

const SceneCopy = ({ act, ended, onReplay }) => (
  <div className="relative">
    {act.kicker && (
      <p
        data-testid={`demo-admin-story-kicker-${act.id}`}
        className="text-[11px] font-semibold uppercase tracking-[0.32em] text-tangerine"
      >
        {act.kicker}
      </p>
    )}
    <h1
      data-testid={`demo-admin-story-title-${act.id}`}
      className={`${act.kicker ? "mt-4" : ""} font-serif text-3xl leading-[1.05] tracking-tight text-ink lg:text-5xl`}
    >
      {act.title}
    </h1>
    {act.subtitle && (
      <p
        data-testid={`demo-admin-story-subtitle-${act.id}`}
        className="mt-5 max-w-xl text-[16px] leading-relaxed text-ink/70"
      >
        {act.subtitle}
      </p>
    )}
    {(act.id === "06" || ended) && (
      <div className="mt-9 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onReplay}
          data-testid="demo-admin-story-replay"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-sand transition hover:bg-forest"
        >
          <RotateCcw className="h-4 w-4" strokeWidth={2.2} />
          Play again
        </button>
        <Link
          to="/demo"
          data-testid="demo-admin-story-back-to-demo"
          className="inline-flex items-center gap-2 rounded-full border border-ink/25 bg-bone px-6 py-3 text-sm font-semibold text-ink transition hover:bg-beige"
        >
          Explore the platform
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    )}
  </div>
);

/* ─── Act 01: Review queue ──────────────────────────────────────────── */

const QUEUE_ROWS = [
  { name: "Zohran M.",  school: "UC Berkeley",   category: "Study abroad", amount: 500, wait: "4d 12h",  flag: null },
  { name: "Maya L.",    school: "Stanford",      category: "Textbooks",    amount: 285, wait: "3d 08h",  flag: null },
  { name: "Andre W.",   school: "Morehouse",     category: "Winter gear",  amount: 220, wait: "2d 19h",  flag: null },
  { name: "Kimani T.",  school: "Howard",        category: "Travel home",  amount: 340, wait: "2d 04h",  flag: "hold" },
  { name: "Priya R.",   school: "UT Austin",     category: "Lab supplies", amount: 175, wait: "1d 22h",  flag: null },
  { name: "Diego M.",   school: "UCLA",          category: "Move-in",      amount: 415, wait: "1d 08h",  flag: null },
];

const ActQueue = ({ actProgress }) => {
  // Reveal rows progressively
  const rowsVisible = Math.min(
    QUEUE_ROWS.length,
    Math.round((actProgress / 0.75) * QUEUE_ROWS.length)
  );
  const highlightRow = actProgress > 0.85;

  return (
    <BrowserChrome url="myrichaunt.org/admin · review queue">
      <div className="p-5 lg:p-6">
        <div className="flex items-baseline justify-between border-b border-border pb-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink">
              Campaign review · queue
            </p>
            <p className="mt-1 text-[12px] text-olive">
              Every campaign lands here before it goes live.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-tangerine/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-tangerine ring-1 ring-inset ring-tangerine/25">
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                className="h-1.5 w-1.5 rounded-full bg-tangerine"
              />
              {QUEUE_ROWS.length} pending
            </span>
          </div>
        </div>

        <table className="mt-3 w-full text-[12.5px] text-ink">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-olive/80">
              <th className="py-2 text-left font-semibold">Student</th>
              <th className="py-2 text-left font-semibold">Category</th>
              <th className="py-2 text-right font-semibold">Ask</th>
              <th className="py-2 text-right font-semibold">Wait</th>
              <th className="py-2 text-right font-semibold">State</th>
            </tr>
          </thead>
          <tbody>
            {QUEUE_ROWS.map((r, i) => {
              const visible = i < rowsVisible;
              const isFirst = i === 0;
              const glow = highlightRow && isFirst;
              return (
                <motion.tr
                  key={r.name}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{
                    opacity: visible ? 1 : 0,
                    y: visible ? 0 : 6,
                    backgroundColor: glow ? "rgba(3,34,244,0.05)" : "rgba(3,34,244,0)",
                  }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-border/60"
                >
                  <td className="py-2.5">
                    <p className="font-semibold text-ink">{r.name}</p>
                    <p className="text-[10.5px] text-olive">{r.school}</p>
                  </td>
                  <td className="py-2.5 text-olive">{r.category}</td>
                  <td className="py-2.5 text-right font-mono text-ink">${r.amount}</td>
                  <td className="py-2.5 text-right font-mono text-olive">{r.wait}</td>
                  <td className="py-2.5 text-right">
                    {r.flag === "hold" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-tangerine/12 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-tangerine ring-1 ring-inset ring-tangerine/25">
                        <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.4} /> flag
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-olive">
                        pending
                      </span>
                    )}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-olive/80">
          <span>Sorted by wait time · desc</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" strokeWidth={2} />
            refreshed just now
          </span>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 02: Six automated checks ──────────────────────────────────── */

const CHECKS = [
  { icon: BookOpen,   label: "NSC enrollment verified",     detail: "UC Berkeley · Spring 2026" },
  { icon: Mail,       label: ".edu email confirmed",         detail: "zohran.m@berkeley.edu" },
  { icon: FileText,   label: "Story reviewed",               detail: "342 words · unique · on-topic" },
  { icon: FileText,   label: "Supporting docs attached",     detail: "2 PDFs · program invoice + visa quote" },
  { icon: Tag,        label: "Category tagged",              detail: "Study abroad" },
  { icon: Landmark,   label: "Stripe payout account linked", detail: "acct_1O***abc · verified" },
];

const ActChecks = ({ actProgress }) => {
  const t = actProgress;
  const rowsChecked = Math.min(
    CHECKS.length,
    Math.round((t / 0.85) * CHECKS.length)
  );
  const allGreen = t > 0.9;

  return (
    <BrowserChrome url="myrichaunt.org/admin/campaign/z-m-2401">
      <div className="p-5 lg:p-6">
        {/* header */}
        <div className="flex items-start justify-between gap-4 border-b border-border pb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">← queue</p>
            <h3 className="mt-1 font-serif text-xl leading-tight text-ink lg:text-2xl">
              Nairobi, or bust.
            </h3>
            <p className="text-[11px] text-olive">Zohran M. · UC Berkeley '27 · study abroad · $500</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-tangerine/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-tangerine ring-1 ring-inset ring-tangerine/25">
            Pending review
          </span>
        </div>

        {/* Six checks */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink">
            Automated verification
          </p>
          <div className="mt-3 space-y-2">
            {CHECKS.map((c, i) => {
              const Icon = c.icon;
              const checked = i < rowsChecked;
              return (
                <motion.div
                  key={c.label}
                  animate={{
                    borderColor: checked ? "rgba(45,180,49,0.35)" : "rgba(26,23,20,0.10)",
                    backgroundColor: checked ? "rgba(45,180,49,0.05)" : "rgba(255,255,255,0)",
                  }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-3 rounded-md border p-3"
                >
                  <Icon className="h-4 w-4 shrink-0 text-olive" strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-ink">{c.label}</p>
                    <p className="truncate font-mono text-[11px] text-olive">{c.detail}</p>
                  </div>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{
                      scale: checked ? 1 : 0,
                      backgroundColor: checked ? "rgb(45,180,49)" : "rgba(45,180,49,0)",
                    }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="flex h-5 w-5 items-center justify-center rounded-full"
                  >
                    <Check className="h-3 w-3 text-bone" strokeWidth={3} />
                  </motion.span>
                </motion.div>
              );
            })}
          </div>

          {/* Summary chip */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: allGreen ? 1 : 0,
              y: allGreen ? 0 : 8,
            }}
            transition={{ duration: 0.4 }}
            className="mt-4 flex items-center justify-between rounded-md bg-grass/10 px-4 py-2.5 ring-1 ring-inset ring-grass/30"
          >
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-grass">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.4} />
              All 6 checks green · ready for reviewer
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 text-[10px] font-semibold text-sand">
              Assign reviewer <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
            </span>
          </motion.div>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 03: Fraud flag ────────────────────────────────────────────── */

const ActFraudFlag = ({ actProgress }) => {
  const t = actProgress;
  const bannerIn = t > 0.1;
  const detailsIn = t > 0.3;
  const escalateActive = t > 0.75;

  return (
    <BrowserChrome url="myrichaunt.org/admin/campaign/k-t-2394">
      <div className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-4 border-b border-border pb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">← queue</p>
            <h3 className="mt-1 font-serif text-xl leading-tight text-ink lg:text-2xl">
              A quick trip home to Detroit.
            </h3>
            <p className="text-[11px] text-olive">Kimani T. · Howard University · travel home · $340</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-scarlet/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-scarlet ring-1 ring-inset ring-scarlet/25">
            <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.4} /> Flagged
          </span>
        </div>

        {/* Warning banner */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: bannerIn ? 1 : 0, y: bannerIn ? 0 : 6 }}
          transition={{ duration: 0.4 }}
          className="mt-4 rounded-md bg-scarlet/[0.06] p-4 ring-1 ring-inset ring-scarlet/25"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-scarlet" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-scarlet">
                Enrollment mismatch
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink">
                Campaign claims{" "}
                <span className="font-mono font-semibold">Spring 2026</span> semester.
                NSC returns{" "}
                <span className="font-mono font-semibold">Fall 2025</span> as last active term for this student.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Details panel */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: detailsIn ? 1 : 0, y: detailsIn ? 0 : 6 }}
          transition={{ duration: 0.4 }}
          className="mt-3 rounded-md border border-border p-4"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
            Cross-check · similar past campaigns
          </p>
          <div className="mt-2 flex items-center justify-between text-[12px] text-ink">
            <span className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-olive" strokeWidth={2} />
              Campaign <span className="font-mono">#2148</span> · story similarity{" "}
              <span className="font-mono font-semibold text-tangerine">84%</span>
            </span>
            <span className="text-[11px] text-olive">Aug 4 · rejected</span>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <motion.button
            type="button"
            animate={{
              backgroundColor: escalateActive ? "rgb(253,97,6)" : "rgba(253,97,6,0)",
              color: escalateActive ? "rgb(250,246,236)" : "rgb(253,97,6)",
              borderColor: "rgb(253,97,6)",
            }}
            transition={{ duration: 0.35 }}
            className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[11px] font-semibold"
          >
            <ArrowUpRight className="h-3 w-3" strokeWidth={2.4} />
            Escalate to founder
          </motion.button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-1.5 text-[11px] font-semibold text-ink"
          >
            <Mail className="h-3 w-3" strokeWidth={2.2} />
            Request info from student
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-1.5 text-[11px] font-semibold text-ink"
          >
            <X className="h-3 w-3" strokeWidth={2.4} />
            Reject
          </button>
        </div>

        {/* Post-click status chip */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{
            opacity: escalateActive && actProgress > 0.9 ? 1 : 0,
            y: escalateActive && actProgress > 0.9 ? 0 : 6,
          }}
          transition={{ duration: 0.35 }}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-ink px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sand"
        >
          <History className="h-3 w-3" strokeWidth={2.4} />
          Escalated · awaits Terri · logged
        </motion.div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 04: Approved with audit trail ─────────────────────────────── */

const AUDIT_ROWS = [
  { at: "08:12", who: "System",       what: "Auto-checks passed",       tone: "info" },
  { at: "08:14", who: "Marie MV",     what: "Reviewer assigned",        tone: "info" },
  { at: "14:22", who: "Terri James",  what: "Approved (all 6 green)",   tone: "good" },
  { at: "14:22", who: "System",       what: "Published to public wall", tone: "info" },
];

const ActAuditTrail = ({ actProgress }) => {
  const t = actProgress;
  const flipped = t > 0.25;
  const auditIn = (i) => t > 0.4 + i * 0.13;
  return (
    <BrowserChrome url="myrichaunt.org/admin/campaign/m-l-2402">
      <div className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-4 border-b border-border pb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">← queue</p>
            <h3 className="mt-1 font-serif text-xl leading-tight text-ink lg:text-2xl">
              Biology textbooks for spring.
            </h3>
            <p className="text-[11px] text-olive">Maya L. · Stanford '27 · textbooks · $285</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Pending pill (fades out) */}
            <motion.span
              animate={{ opacity: flipped ? 0 : 1, scale: flipped ? 0.9 : 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-1 rounded-full bg-tangerine/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-tangerine ring-1 ring-inset ring-tangerine/25"
            >
              Pending review
            </motion.span>
            {/* Approved pill (fades in) */}
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: flipped ? 1 : 0,
                scale: flipped ? 1 : 0.9,
              }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-1.5 rounded-full bg-grass/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-grass ring-1 ring-inset ring-grass/30"
            >
              <Check className="h-3 w-3" strokeWidth={2.8} /> Approved &amp; live
            </motion.span>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink">
            Audit trail · this campaign
          </p>
          <div className="mt-3 overflow-hidden rounded-md border border-border">
            {AUDIT_ROWS.map((row, i) => {
              const visible = auditIn(i);
              const good = row.tone === "good";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: visible ? 1 : 0, x: visible ? 0 : -6 }}
                  transition={{ duration: 0.35 }}
                  className={`grid grid-cols-[64px_1fr_auto] items-center gap-3 px-4 py-2.5 text-[12px] ${
                    i < AUDIT_ROWS.length - 1 ? "border-b border-border/60" : ""
                  } ${good ? "bg-grass/[0.04]" : "bg-bone"}`}
                >
                  <span className="font-mono text-[11px] text-olive">{row.at}</span>
                  <span className={`truncate ${good ? "font-semibold text-ink" : "text-ink"}`}>
                    {row.what}
                  </span>
                  <span className="text-[11px] text-olive">{row.who}</span>
                </motion.div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] italic text-olive">
            Every action, timestamped. Immutable ledger, exportable to the board on demand.
          </p>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 05: Disbursement schedule ─────────────────────────────────── */

const DISBURSEMENTS = [
  { wk: "Wk 1", date: "Apr 3",  amt: 125, status: "Sent",       tx: "tr_1O***abc" },
  { wk: "Wk 2", date: "Apr 10", amt: 125, status: "Sent",       tx: "tr_1O***def" },
  { wk: "Wk 3", date: "Apr 17", amt: 125, status: "Scheduled",  tx: "—" },
  { wk: "Wk 4", date: "Apr 24", amt: 125, status: "Scheduled",  tx: "—" },
];

const ActDisbursement = ({ actProgress }) => {
  const t = actProgress;
  const rowIn = (i) => t > 0.15 + i * 0.13;
  return (
    <BrowserChrome url="myrichaunt.org/admin · disbursements">
      <div className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-4 border-b border-border pb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">disbursement schedule</p>
            <h3 className="mt-1 font-serif text-xl leading-tight text-ink lg:text-2xl">
              Zohran M. · $500 · goal reached
            </h3>
            <p className="text-[11px] text-olive">Released in 4 weekly Stripe transfers to student's payout account.</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-grass/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-grass ring-1 ring-inset ring-grass/30">
            <Check className="h-3 w-3" strokeWidth={2.8} /> On track
          </span>
        </div>

        <table className="mt-4 w-full text-[12.5px] text-ink">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-olive/80">
              <th className="py-2 text-left font-semibold">Week</th>
              <th className="py-2 text-left font-semibold">Send date</th>
              <th className="py-2 text-right font-semibold">Amount</th>
              <th className="py-2 text-right font-semibold">Status</th>
              <th className="py-2 text-right font-semibold">Stripe transfer</th>
            </tr>
          </thead>
          <tbody>
            {DISBURSEMENTS.map((d, i) => {
              const visible = rowIn(i);
              const sent = d.status === "Sent";
              return (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 6 }}
                  transition={{ duration: 0.32 }}
                  className={i < DISBURSEMENTS.length - 1 ? "border-b border-border/60" : ""}
                >
                  <td className="py-2.5 font-mono text-olive">{d.wk}</td>
                  <td className="py-2.5 font-mono text-ink">{d.date}</td>
                  <td className="py-2.5 text-right font-mono font-semibold text-ink">${d.amt}</td>
                  <td className="py-2.5 text-right">
                    {sent ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-grass/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-grass ring-1 ring-inset ring-grass/25">
                        <Check className="h-2.5 w-2.5" strokeWidth={2.8} /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-cobalt/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-cobalt ring-1 ring-inset ring-cobalt/25">
                        <Clock className="h-2.5 w-2.5" strokeWidth={2.4} /> Scheduled
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 text-right font-mono text-[10.5px] text-olive">{d.tx}</td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-4 flex items-center justify-between rounded-md bg-ink/[0.04] px-4 py-2.5 text-[11px] text-olive">
          <span className="italic">Auto-paused if any flag reopens on this campaign.</span>
          <span className="font-mono">$250 sent · $250 scheduled</span>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── End of file — Act 06 is rendered via <DemoEndSlate /> at the top-level
       conditional in the reel's <section>. See imports above. ─────────── */
